/**
 * Execution Overview Component
 *
 * Shows overview of a selected execution from history.
 * Displays all nodes and their execution status/results.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  Image,
  FileText,
  X,
  AlertTriangle,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react';
import { StepResultDisplay } from './StepResultDisplay';
import type { WorkflowExecution, ExecutionStep, StepStatus } from '@/types/imageWorkflow';
import { useUsersBatch } from '../hooks/useUsersBatch';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { getExecutionStepKey, normalizeExecutionSteps } from '../utils/executionSteps';

// Status configuration
const statusConfig: Record<
  StepStatus,
  { label: string; color: string; bgColor: string; icon: React.ReactNode }
> = {
  pending: {
    label: 'Pending',
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    icon: <Clock className="w-4 h-4" />,
  },
  running: {
    label: 'Running',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: <Loader2 className="w-4 h-4" />,
  },
  completed: {
    label: 'Completed',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  failed: {
    label: 'Failed',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: <XCircle className="w-4 h-4" />,
  },
  skipped: {
    label: 'Skipped',
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
    icon: <Clock className="w-4 h-4" />,
  },
};

const executionStatusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', color: 'text-gray-500', icon: <Clock className="w-5 h-5" /> },
  running: { label: 'Running', color: 'text-blue-600', icon: <Loader2 className="w-5 h-5 animate-spin" /> },
  completed: { label: 'Completed', color: 'text-green-600', icon: <CheckCircle2 className="w-5 h-5" /> },
  failed: { label: 'Failed', color: 'text-red-600', icon: <XCircle className="w-5 h-5" /> },
  partial: { label: 'Partial', color: 'text-yellow-600', icon: <AlertTriangle className="w-5 h-5" /> },
  cancelled: { label: 'Cancelled', color: 'text-gray-500', icon: <XCircle className="w-5 h-5" /> },
};

interface ExecutionOverviewProps {
  execution: WorkflowExecution;
  /** Callback when user clicks resume - passes execution ID, user run ID, and resume node key */
  onResume?: (executionId: string, userRunId: string, resumeNodeKey: string) => void;
  /** Whether resume is currently in progress */
  isResuming?: boolean;
}

export const ExecutionOverview: React.FC<ExecutionOverviewProps> = ({
  execution,
  onResume,
  isResuming = false,
}) => {
  const { t } = useI18n();
  const [selectedStep, setSelectedStep] = useState<ExecutionStep | null>(null);
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUserRunId, setSelectedUserRunId] = useState<string | null>(null);

  // Reset local state when execution changes
  useEffect(() => {
    setSelectedStep(null);
    setSelectedUserRunId(null);
    setShowUserList(false);
  }, [execution.id]);

  // Extract all target user IDs
  const targetUserIds = useMemo(() => {
    return execution.user_runs?.map((ur) => ur.target_user_id) ?? [];
  }, [execution.user_runs]);

  // Fetch user info in batch
  const { users: userInfoMap, loading: loadingUsers } = useUsersBatch(targetUserIds, 'display');

  // Helper to format user display
  const formatUserDisplay = (userId: string) => {
    const userInfo = userInfoMap[userId];

    if (!userInfo) {
      return {
        primary: userId.slice(0, 12) + '...',
        secondary: userId,
        hasRealName: false,
      };
    }

    // Check if it's UserDisplayInfo (has display_name)
    if ('display_name' in userInfo) {
      const hasRealName = !!userInfo.birth_chart_name;
      return {
        primary: hasRealName
          ? `${userInfo.birth_chart_name} (@${userInfo.username})`
          : `${userInfo.nickname} (@${userInfo.username})`,
        secondary: userId,
        hasRealName,
      };
    }

    // UserBasicInfo
    return {
      primary: `${userInfo.nickname} (@${userInfo.username})`,
      secondary: userId,
      hasRealName: false,
    };
  };

  // Simplified getter for inline usage
  const getUserDisplayName = (userId: string) => {
    return formatUserDisplay(userId).primary;
  };

  // Get selected user run (default to first one)
  const selectedUserRun = selectedUserRunId
    ? execution.user_runs?.find((ur) => ur.id === selectedUserRunId)
    : execution.user_runs?.[0];

  const steps = useMemo(
    () => normalizeExecutionSteps(selectedUserRun?.steps ?? []),
    [selectedUserRun?.steps]
  );
  const statusInfo = executionStatusConfig[execution.status] ?? executionStatusConfig.pending;

  useEffect(() => {
    if (!selectedStep) return;
    const currentStepKey = getExecutionStepKey(selectedStep);
    const refreshedStep = steps.find((step) => getExecutionStepKey(step) === currentStepKey);
    if (refreshedStep !== selectedStep) {
      setSelectedStep(refreshedStep ?? null);
    }
  }, [steps, selectedStep]);

  // Calculate stats
  const completedSteps = steps.filter((s) => s.status === 'completed').length;
  const failedSteps = steps.filter((s) => s.status === 'failed').length;

  // Check if resumable
  const isResumable = ['failed', 'partial'].includes(execution.status) && selectedUserRun && onResume;
  const failedStep = steps.find((s) => s.status === 'failed');
  const resumeNodeKey = selectedUserRun?.error_node_key || failedStep?.node_key;

  // Handle resume click
  const handleResume = () => {
    if (!selectedUserRun || !resumeNodeKey || !onResume) return;
    onResume(execution.id, selectedUserRun.id, resumeNodeKey);
  };

  // Handle user selection
  const handleUserClick = (userRunId: string) => {
    setSelectedUserRunId(userRunId);
    setSelectedStep(null); // Clear step selection when switching users
  };

  // Format duration
  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return parseBackendDateTime(dateString).toLocaleString();
  };

  return (
    <div className="h-full flex flex-col">
      {/* Execution header */}
      <div className="mb-4 p-4 bg-white rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className={statusInfo.color}>{statusInfo.icon}</span>
            <div>
              <h3 className="font-semibold text-gray-900">
                Execution #{execution.id.slice(0, 8)}
              </h3>
              <p className="text-sm text-gray-500">{formatDate(execution.created_at)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isResumable && resumeNodeKey && (
              <button
                onClick={handleResume}
                disabled={isResuming}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  isResuming
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {isResuming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                {isResuming ? t('imageWorkflows.executionOverview.retrying') : t('imageWorkflows.executionOverview.retryFrom').replace('{node}', resumeNodeKey)}
              </button>
            )}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color} bg-gray-100`}>
              {statusInfo.label}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-gray-500">Duration: </span>
            <span className="font-medium">{formatDuration(execution.total_duration_ms)}</span>
          </div>
          <div>
            <span className="text-gray-500">Steps: </span>
            <span className="font-medium text-green-600">{completedSteps} completed</span>
            {failedSteps > 0 && (
              <span className="font-medium text-red-600 ml-1">/ {failedSteps} failed</span>
            )}
          </div>
          {execution.user_runs && execution.user_runs.length > 0 && (
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => setShowUserList(!showUserList)}
                className="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
              >
                {showUserList ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <span>{t('imageWorkflows.executionOverview.users').replace('{count}', String(execution.user_runs.length))}</span>
              </button>
            </div>
          )}
          {selectedUserRun && execution.user_runs && execution.user_runs.length > 1 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded">
              <span className="text-xs text-blue-700">
                {t('imageWorkflows.executionOverview.viewUser').replace('{user}', getUserDisplayName(selectedUserRun.target_user_id))}
              </span>
            </div>
          )}
        </div>

        {/* Target Users List (Collapsible) */}
        {showUserList && execution.user_runs && execution.user_runs.length > 0 && (
          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t('imageWorkflows.executionOverview.targetUserDetails')}</h4>
            <div className="max-h-60 overflow-y-auto space-y-1.5">
              {execution.user_runs.map((userRun) => {
                const isSelected = selectedUserRun?.id === userRun.id;
                const runStatusInfo = executionStatusConfig[userRun.status] ?? executionStatusConfig.pending;
                const userDisplay = formatUserDisplay(userRun.target_user_id);

                return (
                  <button
                    key={userRun.id}
                    onClick={() => handleUserClick(userRun.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className={`${runStatusInfo.color} flex-shrink-0`}>
                        {runStatusInfo.icon}
                      </span>
                      <div className="flex flex-col items-start min-w-0 flex-1">
                        <span className="text-sm font-medium text-gray-900 truncate w-full">
                          {loadingUsers ? (
                            <span className="text-gray-400">{t('imageWorkflows.executionOverview.loading')}</span>
                          ) : (
                            userDisplay.primary
                          )}
                        </span>
                        <span className="text-xs font-mono text-gray-500 truncate w-full">
                          {userDisplay.secondary}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <span className="text-xs text-gray-500">
                        {userRun.completed_steps}/{userRun.total_steps}
                      </span>
                      {userRun.total_duration_ms && (
                        <span className="text-xs text-gray-400">
                          {formatDuration(userRun.total_duration_ms)}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {t('imageWorkflows.executionOverview.totalUsers')
                .replace('{total}', String(execution.user_runs.length))
                .replace('{completed}', String(execution.completed_users))
                .replace('{failed}', String(execution.failed_users))}
              {selectedUserRun && (
                <span className="text-blue-600 ml-2">
                  · {t('imageWorkflows.executionOverview.currentView').replace('{user}', getUserDisplayName(selectedUserRun.target_user_id))}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Error summary */}
        {execution.error_summary && (
          <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-600">
            <div className="font-medium mb-1">Overall Error:</div>
            {execution.error_summary}
          </div>
        )}

        {/* Current user error */}
        {selectedUserRun?.error_message && (
          <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-600">
            <div className="font-medium mb-1">
              {t('imageWorkflows.executionOverview.userError').replace('{user}', getUserDisplayName(selectedUserRun.target_user_id))}
              {selectedUserRun.error_node_key && ` at ${selectedUserRun.error_node_key}`}:
            </div>
            {selectedUserRun.error_message}
          </div>
        )}
      </div>

      {/* Steps list and detail */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left: Steps list */}
        <div className="w-64 overflow-y-auto space-y-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Execution Steps</h4>
            {selectedUserRun && execution.user_runs && execution.user_runs.length > 1 && (
              <span className="text-xs text-gray-500">
                User: {selectedUserRun.target_user_id.slice(-8)}
              </span>
            )}
          </div>
          {steps.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No steps recorded</p>
          ) : (
            steps.map((step, index) => {
              const config = statusConfig[step.status as StepStatus] ?? statusConfig.pending;
              const stepKey = getExecutionStepKey(step);
              const isSelected = selectedStep ? getExecutionStepKey(selectedStep) === stepKey : false;

              return (
                <button
                  key={stepKey}
                  onClick={() => setSelectedStep(isSelected ? null : step)}
                  className={`w-full text-left p-2 rounded-lg border transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full text-xs ${config.bgColor} ${config.color}`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-800 truncate">
                        {step.node_key}
                      </div>
                      {step.duration_ms && (
                        <div className="text-xs text-gray-400">
                          {formatDuration(step.duration_ms)}
                        </div>
                      )}
                    </div>
                    <span className={config.color}>{config.icon}</span>
                  </div>

                  {/* Quick preview */}
                  {step.status === 'completed' && step.output_data && (
                    <div className="mt-1 ml-7 text-xs text-gray-500">
                      {!!(step.output_data as Record<string, unknown>).image_url && (
                        <span className="flex items-center gap-1">
                          <Image className="w-3 h-3" /> Image
                        </span>
                      )}
                      {!!(step.output_data as Record<string, unknown>).prompt && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" /> Prompt
                        </span>
                      )}
                    </div>
                  )}

                  {step.status === 'failed' && step.error_message && (
                    <div className="mt-1 ml-7 text-xs text-red-500 truncate">
                      {step.error_message}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* Right: Step detail */}
        <div className="flex-1 overflow-y-auto bg-white rounded-lg border p-4">
          {selectedStep ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-800">
                  {selectedStep.node_key} - Details
                </h4>
                <button
                  onClick={() => setSelectedStep(null)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <StepResultDisplay step={selectedStep} />
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a step to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExecutionOverview;
